import React, { useState,useMemo  } from "react";
import { Container } from "reactstrap";
import FRMTopbar from "./components/FRMTopbar";
import FRMNav from "./components/FRMNav";
import FleetOverviewCard from "./components/FleetOverviewCard";
import VesselRankList from "./components/VesselRankList";
import VesselScoreCard from "./components/VesselScoreCard";
import RiskOverview from "./components/RiskOverview";
import FleetRiskByEquipment from "./components/FleetRiskByEquipment";
import RiskTriggerCardSetup from "./RiskTriggerCardSetup";
import RiskTriggerCards from "./RiskTriggerCards";
import PageGrid from "../../components/GridLayout/PageGrid";

import {
  FleetOverviewEditor,
  VesselRankEditor,
  VesselScoreEditor,
  RiskOverviewEditor,
  EquipmentEditor,
} from "./components/WidgetDataEditor";
import "./FleetRisk.css";

const TABS = ["Dashboard", "Live Data", "Risk Trigger Cards", "Risk Trigger Card Setup", "Risk Trigger Summary", "Alerts"];

// ── Default data for each widget type ────────────────────────────
const DEFAULT_OVERVIEW = {
  fleetRisk: 78, badge: "Elevated Attention",
  description: "Move from isolated fuel saving initiatives to a machinery-aware, berth-aware, and weather-aware operating system that continuously chooses the best voyage plan, speed envelope, equipment loading pattern, and maintenance window.",
  highRisk: "1", highRiskNote: "Flora Schulte is driving most of the fleet risk",
  openItems: "8", openItemsNote: "6 items need quick engineering follow-up",
  repairExposure: "$370K", exposureNote: "Current cost exposure across the sister fleet",
};

const DEFAULT_RANKED = {
  vessels: [
    { rank: 1, name: "FRIEDRICH SCHULTE", location: "BUSAN",     date: "24/03/2026, 11:28:15", cost: "$46K", desc: "Multiple cylinders on the MAN S550ME-B9.3 main engine are exhibiting high jacket cooling water outlet and exhaust gas..." },
    { rank: 2, name: "FLORA SCHULTE",     location: "SINGAPORE", date: "23/03/2026, 17:47:04", cost: "$58K", desc: "Main engine cylinder 1 exhaust gas temperature has consistently exceeded 210°C while shaft power remains above 300 kW..." },
    { rank: 3, name: "FREYA SCHULTE",     location: "ADELAIDE",  date: "23/03/2026, 17:19:24", cost: "$24K", desc: "All main engine cylinders on MAN S550ME-B9.3 exhibit exhaust gas temperatures significantly above normal (>380°C)..." },
  ],
};

const DEFAULT_VESSELS = {
  vessels: [
    { id: 1, name: "FLORA SCHULTE",     risk: "HIGH",   score: 71, desc: "Main engine cylinder combustion imbalance is the main driver.",     openItems: 3, exposure: "$168K", savings: "$22K" },
    { id: 2, name: "FRIEDRICH SCHULTE", risk: "MEDIUM", score: 66, desc: "Turbocharger efficiency loss is the main system to watch.",          openItems: 3, exposure: "$112K", savings: "$17K" },
    { id: 3, name: "FREYA SCHULTE",     risk: "LOW",    score: 31, desc: "Boiler tuning is the main opportunity, not an urgent failure risk.", openItems: 2, exposure: "$69K",  savings: "$11K" },
  ],
};

const DEFAULT_RISK_OVERVIEW = {
  vessels: [
    { name: "FLORA SCHULTE",     trend: "Worsening", weeks: [0, 1, 2, 3], current: 3 },
    { name: "FRIEDRICH SCHULTE", trend: "Watch",     weeks: [0, 1, 2, 3], current: 3 },
    { name: "FREYA SCHULTE",     trend: "Improving", weeks: [0, 1, 3, 2], current: 2 },
  ],
};

const DEFAULT_EQUIPMENT = {
  equipment: [
    { name: "Main Engine",  risk: "HIGH",   desc: "Highest fleet exposure and strongest impact on reliability." },
    { name: "Turbocharger", risk: "MEDIUM", desc: "Watch fouling, air path restriction and efficiency drift." },
    { name: "Aux Engines",  risk: "MEDIUM", desc: "Fuel pump calibration and performance drift need follow-up." },
    { name: "Boiler",       risk: "LOW",    desc: "Stable overall, but clear tuning opportunity remains." },
  ],
};

// ── Load from localStorage or use defaults ────────────────────────
const LS = "frm_widget_data";
const loadData = () => {
  try { const s = localStorage.getItem(LS); return s ? JSON.parse(s) : null; } catch { return null; }
};
const saveData = (d) => { try { localStorage.setItem(LS, JSON.stringify(d)); } catch {} };

const initData = () => {
  const saved = loadData();
  return {
    overview:     saved?.overview     || { ...DEFAULT_OVERVIEW },
    ranked:       saved?.ranked       || { ...DEFAULT_RANKED },
    vessels:      saved?.vessels      || { ...DEFAULT_VESSELS },
    riskOverview: saved?.riskOverview || { ...DEFAULT_RISK_OVERVIEW },
    equipment:    saved?.equipment    || { ...DEFAULT_EQUIPMENT },
    // extra duplicates
    extras:       saved?.extras       || [],
  };
};

// ── Widget ID counter for duplicates ──────────────────────────────
// let _extraId = 1;
// const nextId = () => `extra_${_extraId++}`;

// ── Maps widget type to its editor and renderer ───────────────────
const WIDGET_TYPES = {
  overview:     { label: "Fleet Risk Overview",       icon: "mdi mdi-gauge" },
  ranked:       { label: "Vessel Risk Ranking",        icon: "mdi mdi-format-list-numbered" },
  vessels:      { label: "Vessel Score Cards",         icon: "mdi mdi-card-account-details-outline" },
  riskOverview: { label: "Risk Overview",              icon: "mdi mdi-chart-timeline-variant" },
  equipment:    { label: "Fleet Risk By Equipment",    icon: "mdi mdi-engine-outline" },
};
// const extraIdRef = React.useRef(1);
// const nextId = () => `extra_${Date.now()}_${extraIdRef.current++}`;
const renderWidget = (type, data) => {
  switch (type) {
    case "overview":
      return (
        <div className="frm-card" style={{ height: "100%" }}>
          <FleetOverviewCard
            fleetRisk={data.fleetRisk}
            highRisk={data.highRisk}
            openItems={data.openItems}
            repairExposure={data.repairExposure}
          />
        </div>
      );
    case "ranked":
      return <VesselRankList vessels={data.vessels} />;
    case "vessels":
      return (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, padding: 12 }}>
          {data.vessels.map((v, i) => <VesselScoreCard key={i} vessel={v} />)}
        </div>
      );
    case "riskOverview":
      return <RiskOverview vessels={data.vessels} />;
    case "equipment":
      return <FleetRiskByEquipment equipment={data.equipment} />;
    default:
      return null;
  }
};

const renderEditor = (type, data, onChange) => {
  switch (type) {
    case "overview":     return <FleetOverviewEditor data={data} onChange={onChange} />;
    case "ranked":       return <VesselRankEditor    data={data} onChange={onChange} />;
    case "vessels":      return <VesselScoreEditor   data={data} onChange={onChange} />;
    case "riskOverview": return <RiskOverviewEditor  data={data} onChange={onChange} />;
    case "equipment":    return <EquipmentEditor      data={data} onChange={onChange} />;
    default:             return null;
  }
};

// ── Default layout positions ───────────────────────────────────────
const BASE_WIDGETS_DEF = [
  { id: "overview",     type: "overview",     defaultW: 6,  defaultH: 7,  minH: 5, minW: 4, defaultX: 0, defaultY: 0  },
  { id: "ranked",       type: "ranked",       defaultW: 6,  defaultH: 7,  minH: 5, minW: 4, defaultX: 6, defaultY: 0  },
  { id: "vessels",      type: "vessels",      defaultW: 12, defaultH: 5,  minH: 4, minW: 6, defaultX: 0, defaultY: 7  },
  { id: "riskOverview", type: "riskOverview", defaultW: 6,  defaultH: 8,  minH: 6, minW: 4, defaultX: 0, defaultY: 12 },
  { id: "equipment",    type: "equipment",    defaultW: 6,  defaultH: 8,  minH: 6, minW: 4, defaultX: 6, defaultY: 12 },
];

// ════════════════════════════════════════════════════════════════════
const FleetRiskDashboard = () => {

const extraIdRef = React.useRef(1);
const nextId = () => `extra_${Date.now()}_${extraIdRef.current++}`;
  const [activeTab,    setActiveTab]    = useState("Dashboard");
  const [activeVessel, setActiveVessel] = useState("FRIEDRICH SCHULTEqwdw");
  const [widgetData,   setWidgetData]   = useState(initData);
const [selectedMachine, setSelectedMachine] = useState("");
  document.title = "Fleet Risk Management";

  // ── Update a base widget's data and persist ───────────────────
  const updateBase = (key, newData) => {
    const next = { ...widgetData, [key]: newData };
    setWidgetData(next);
    saveData(next);
  };

  // ── Update an extra (duplicated) widget's data ────────────────
  const updateExtra = (id, newData) => {
    const next = {
      ...widgetData,
      extras: widgetData.extras.map(e => e.id === id ? { ...e, data: newData } : e),
    };
    setWidgetData(next);
    saveData(next);
  };

  // ── Duplicate a widget (add to extras) ───────────────────────
  const duplicateWidget = (type) => {
    const srcData = widgetData[type];
    const deepCopy = JSON.parse(JSON.stringify(srcData));
    const id = nextId();
    const next = {
      ...widgetData,
      extras: [...widgetData.extras, { id, type, data: deepCopy }],
    };
    setWidgetData(next);
    saveData(next);
  };

  // ── Remove a duplicated widget ────────────────────────────────
  const removeExtra = (id) => {
    const next = { ...widgetData, extras: widgetData.extras.filter(e => e.id !== id) };
    setWidgetData(next);
    saveData(next);
  };

  // ── Reset all data to defaults ────────────────────────────────
  const resetAll = () => {
    const fresh = {
      overview:     { ...DEFAULT_OVERVIEW },
      ranked:       JSON.parse(JSON.stringify(DEFAULT_RANKED)),
      vessels:      JSON.parse(JSON.stringify(DEFAULT_VESSELS)),
      riskOverview: JSON.parse(JSON.stringify(DEFAULT_RISK_OVERVIEW)),
      equipment:    JSON.parse(JSON.stringify(DEFAULT_EQUIPMENT)),
      extras:       [],
    };
    setWidgetData(fresh);
    saveData(fresh);
  };

  // ── Build PageGrid widgets list ───────────────────────────────
  // const buildWidgets = () => {
  //   // base widgets
  //   const base = BASE_WIDGETS_DEF.map(def => ({
  //     ...def,
  //     label: WIDGET_TYPES[def.type].label,
  //     icon:  WIDGET_TYPES[def.type].icon,
  //     content: renderWidget(def.type, widgetData[def.type]),
  //     // customizer = data editor shown in Widgets panel
  //     customizer: renderEditor(
  //       def.type,
  //       widgetData[def.type],
  //       (newData) => updateBase(def.type, newData)
  //     ),
  //   }));

  //   // extra (duplicated) widgets — stacked below base
  //   const extras = widgetData.extras.map((e, idx) => ({
  //     id:       e.id,
  //     type:     e.type,
  //     label:    `${WIDGET_TYPES[e.type]?.label} (copy)`,
  //     icon:     WIDGET_TYPES[e.type]?.icon || "mdi mdi-content-copy",
  //     defaultW: 6, defaultH: 7, minH: 4, minW: 4,
  //     defaultX: (idx % 2) * 6,
  //     defaultY: 20 + Math.floor(idx / 2) * 8,
  //     content: (
  //       <div style={{ position: "relative", height: "100%" }}>
  //         {renderWidget(e.type, e.data)}
  //         {/* Remove button shown always on copies */}
  //         <button
  //           onClick={() => removeExtra(e.id)}
  //           title="Remove this copy"
  //           style={{
  //             position: "absolute", top: 6, right: 6,
  //             background: "#fef2f2", border: "0.5px solid #f7c1c1",
  //             borderRadius: 6, color: "#a32d2d", fontSize: 11,
  //             padding: "3px 8px", cursor: "pointer", zIndex: 5,
  //             display: "flex", alignItems: "center", gap: 4,
  //           }}>
  //           ✕ Remove
  //         </button>
  //       </div>
  //     ),
  //     customizer: renderEditor(e.type, e.data, (newData) => updateExtra(e.id, newData)),
  //   }));

  //   return [...base, ...extras];
  // };



// REPLACE buildWidgets() function call inside renderTabContent with useMemo:
// Remove the buildWidgets function definition entirely.
// Add this INSIDE the component, after the nextId definition:

const widgets = useMemo(() => {
  const base = BASE_WIDGETS_DEF.map(def => ({
    ...def,
    label: WIDGET_TYPES[def.type].label,
    icon:  WIDGET_TYPES[def.type].icon,
    content: renderWidget(def.type, widgetData[def.type]),
    customizer: renderEditor(
      def.type,
      widgetData[def.type],
      (newData) => updateBase(def.type, newData)
    ),
  }));

  const extras = widgetData.extras.map((e, idx) => ({
    id:       e.id,
    type:     e.type,
    label:    `${WIDGET_TYPES[e.type]?.label || e.type} (copy)`,
    icon:     WIDGET_TYPES[e.type]?.icon || "mdi mdi-content-copy",
    defaultW: 6, defaultH: 7, minH: 4, minW: 4,
    defaultX: (idx % 2) * 6,
    defaultY: 20 + Math.floor(idx / 2) * 8,
    content: (
      <div style={{ position: "relative", height: "100%" }}>
        {renderWidget(e.type, e.data)}
        <button
          onClick={() => removeExtra(e.id)}
          title="Remove this copy"
          style={{
            position: "absolute", top: 6, right: 6,
            background: "#fef2f2", border: "0.5px solid #f7c1c1",
            borderRadius: 6, color: "#a32d2d", fontSize: 11,
            padding: "3px 8px", cursor: "pointer", zIndex: 5,
            display: "flex", alignItems: "center", gap: 4,
          }}>
          ✕ Remove
        </button>
      </div>
    ),
    customizer: renderEditor(e.type, e.data, (newData) => updateExtra(e.id, newData)),
  }));

  return [...base, ...extras];
}, [widgetData]); // only rebuilds when data changes

  const duplicatePanel = useMemo(() => (
  <div style={{ marginTop: 12 }}>
    <div style={{ fontSize: 10, fontWeight: 700, color: "#aab0be",
      letterSpacing: 1, marginBottom: 8 }}>DUPLICATE A WIDGET</div>
    <p style={{ fontSize: 11, color: "#8a909e", marginBottom: 10, lineHeight: 1.5 }}>
      Click <strong>+</strong> to add a copy of any widget with independent editable data.
    </p>
    {Object.entries(WIDGET_TYPES).map(([type, meta]) => (
      <div key={type}
        style={{ display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "8px 10px", border: "0.5px solid #e2e4e8", borderRadius: 8,
          marginBottom: 7, background: "#fff" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div style={{ width: 28, height: 28, borderRadius: 6, background: "#e1f5ee",
            display: "flex", alignItems: "center", justifyContent: "center" }}>
            <i className={meta.icon} style={{ fontSize: 13, color: "#1a9e78" }} />
          </div>
          <span style={{ fontSize: 12, color: "#1a1e2e" }}>{meta.label}</span>
        </div>
        <button
          onClick={() => duplicateWidget(type)}
          title={`Duplicate ${meta.label}`}
          style={{ width: 26, height: 26, borderRadius: 6, border: "0.5px solid #1a9e78",
            background: "#e1f5ee", color: "#0f6e56", fontSize: 18, lineHeight: 1,
            cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
          +
        </button>
      </div>
    ))}
    <hr style={{ border: "none", borderTop: "0.5px solid #e2e4e8", margin: "12px 0" }} />
    <button onClick={resetAll}
      style={{ width: "100%", padding: "7px", border: "0.5px solid #f7c1c1",
        borderRadius: 6, background: "#fef2f2", color: "#a32d2d",
        fontSize: 12, cursor: "pointer", fontWeight: 500 }}>
      ↺ Reset all data to defaults
    </button>
  </div>
), [widgetData]); // rebuilds only when data changes
  // const duplicatePanel = (
  //   <div style={{ marginTop: 12 }}>
  //     <div style={{ fontSize: 10, fontWeight: 700, color: "#aab0be",
  //       letterSpacing: 1, marginBottom: 8 }}>DUPLICATE A WIDGET</div>
  //     <p style={{ fontSize: 11, color: "#8a909e", marginBottom: 10, lineHeight: 1.5 }}>
  //       Click <strong>+</strong> to add a copy of any widget with independent editable data.
  //     </p>
  //     {Object.entries(WIDGET_TYPES).map(([type, meta]) => (
  //       <div key={type}
  //         style={{ display: "flex", alignItems: "center", justifyContent: "space-between",
  //           padding: "8px 10px", border: "0.5px solid #e2e4e8", borderRadius: 8,
  //           marginBottom: 7, background: "#fff" }}>
  //         <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
  //           <div style={{ width: 28, height: 28, borderRadius: 6, background: "#e1f5ee",
  //             display: "flex", alignItems: "center", justifyContent: "center" }}>
  //             <i className={meta.icon} style={{ fontSize: 13, color: "#1a9e78" }} />
  //           </div>
  //           <span style={{ fontSize: 12, color: "#1a1e2e" }}>{meta.label}</span>
  //         </div>
  //         <button
  //           onClick={() => duplicateWidget(type)}
  //           title={`Duplicate ${meta.label}`}
  //           style={{ width: 26, height: 26, borderRadius: 6, border: "0.5px solid #1a9e78",
  //             background: "#e1f5ee", color: "#0f6e56", fontSize: 18, lineHeight: 1,
  //             cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
  //             fontWeight: 400 }}>
  //           +
  //         </button>
  //       </div>
  //     ))}
  //     <hr style={{ border: "none", borderTop: "0.5px solid #e2e4e8", margin: "12px 0" }} />
  //     <button onClick={resetAll}
  //       style={{ width: "100%", padding: "7px", border: "0.5px solid #f7c1c1",
  //         borderRadius: 6, background: "#fef2f2", color: "#a32d2d",
  //         fontSize: 12, cursor: "pointer", fontWeight: 500 }}>
  //       ↺ Reset all data to defaults
  //     </button>
  //   </div>
  // );

  const renderTabContent = () => {
    switch (activeTab) {

      case "Dashboard":
        return (
          <div style={{ padding: 16, background: "#f5f6f8", minHeight: "calc(100vh - 96px)" }}>
            <PageGrid
              pageKey="frm_dashboard"
              allowAllUsers={true}
              widgets={widgets}
              extraPanelContent={duplicatePanel}
            />
          </div>
        );

      case "Risk Trigger Cards":      return <RiskTriggerCards />;
      case "Risk Trigger Card Setup": return <RiskTriggerCardSetup selectedMachine={selectedMachine} />;

      case "Live Data":
        return <div style={{ padding: 40, textAlign: "center", color: "#8a909e", fontSize: 14, background: "#f5f6f8" }}>Live Data — coming soon</div>;
      case "Risk Trigger Summary":
        return <div style={{ padding: 40, textAlign: "center", color: "#8a909e", fontSize: 14, background: "#f5f6f8" }}>Risk Trigger Summary — coming soon</div>;
      case "Alerts":
        return <div style={{ padding: 40, textAlign: "center", color: "#8a909e", fontSize: 14, background: "#f5f6f8" }}>Alerts — coming soon</div>;

      default: return null;
    }
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <div className="frm-page">
            <FRMTopbar />
            <FRMNav
              tabs={TABS}
              activeTab={activeTab}
              onTabChange={setActiveTab}
              activeVessel={activeVessel}
              onVesselChange={setActiveVessel}
              showEditDashboard={activeTab === "Dashboard"}
              selectedMachine={selectedMachine}               
  onMachineChange={setSelectedMachine}
            />
            {renderTabContent()}
          </div>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default FleetRiskDashboard;